import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    gstin: "",
    notes: "",
};

/**
 * Reusable add/edit client dialog. Used by the Clients page and inline from the
 * New Task form. `client` (object) puts it in edit mode; `initialName` pre-fills
 * the name when adding. `onSaved(savedClient)` fires with the API response.
 */
export default function ClientFormDialog({
    open,
    onOpenChange,
    client = null,
    initialName = "",
    onSaved,
}) {
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(
            client
                ? {
                      name: client.name || "",
                      contact_person: client.contact_person || "",
                      email: client.email || "",
                      phone: client.phone || "",
                      address: client.address || "",
                      gstin: client.gstin || "",
                      notes: client.notes || "",
                  }
                : { ...empty, name: initialName || "" },
        );
    }, [open, client, initialName]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Client name is required");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            for (const k of Object.keys(payload)) {
                if (k !== "name" && payload[k] === "") payload[k] = null;
            }
            let saved;
            if (client?.id) {
                ({ data: saved } = await api.patch(`/clients/${client.id}`, payload));
                toast.success("Client updated");
            } else {
                ({ data: saved } = await api.post("/clients", payload));
                toast.success("Client added");
            }
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                        {client ? "Edit client" : "Add client"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="space-y-2 md:col-span-2">
                            <Label>Name <span className="text-primary">*</span></Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="client-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contact person</Label>
                            <Input
                                value={form.contact_person}
                                onChange={(e) =>
                                    setForm({ ...form, contact_person: e.target.value })
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>GSTIN</Label>
                            <Input
                                value={form.gstin}
                                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>Address</Label>
                            <Textarea
                                rows={2}
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label>Notes</Label>
                            <Textarea
                                rows={2}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="client-save-button">
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : client ? (
                                "Save changes"
                            ) : (
                                "Add client"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
